/**
 * Calculates the binomial coefficient "n choose k", C(n, k).
 * This implementation is optimized to avoid large intermediate numbers from factorials.
 * It uses the property C(n, k) = C(n, n-k) for efficiency.
 * @param n - The total number of items.
 * @param k - The number of items to choose.
 * @returns The number of combinations.
 */
const combinations = (n: number, k: number): number => {
  if (k < 0 || k > n) {
    return 0
  }
  if (k === 0 || k === n) {
    return 1
  }
  // Use symmetry: C(n, k) === C(n, n-k)
  if (k > n / 2) {
    k = n - k
  }

  let res = 1
  for (let i = 1; i <= k; i++) {
    res = (res * (n - i + 1)) / i
  }
  return res
}

/**
 * Calculates the Binomial Probability Mass Function (PMF).
 * P(X=k) = C(n, k) * p^k * (1-p)^(n-k)
 * @param k - Number of successes (the outcome).
 * @param n - Number of trials.
 * @param p - Probability of success on a single trial.
 * @returns The probability of getting exactly k successes in n trials.
 */
const binomialProbability = (k: number, n: number, p: number): number => {
  const C_nk = combinations(n, k)
  const p_k = Math.pow(p, k)
  const q_nk = Math.pow(1 - p, n - k)
  return C_nk * p_k * q_nk
}

/**
 * Generates all data points for a binomial distribution.
 * @param n - Number of trials.
 * @param p - Probability of success.
 * @param lbound - Lower boundary not used for binomial as n/p already set bounds.
 * @param ubound - Upper boundary not used for binomial as n/p already set bounds.
 * @returns An object with 'labels' (k values) and 'data' (P(X=k) values).
 */
export const binomialSample = (
  n: number,
  p: number,
  lbound?: number,
  ubound?: number,
): { labels: number[]; data: number[] } => {
  const labels = [] // k = 0, 1, 2, ..., n
  const data = [] // P(X=k) for each k

  for (let k = 0; k <= n; k++) {
    labels.push(k)
    data.push(binomialProbability(k, n, p))
  }
  return { labels, data }
}
